/**
 * Product Picker Panel — Visual Editor Side Panel
 *
 * Lets designers choose which real products appear on their homepage
 * product sections (featured_products, new_arrivals, bestsellers, etc.).
 *
 * Shows a searchable list of the designer's products with checkboxes.
 * Selected products replace placeholder data in the template content.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  X, Search, Package, Check, ChevronDown, ChevronRight,
  GripVertical, AlertCircle, ShoppingBag, Star, Sparkles, Image as ImageIcon
} from 'lucide-react';

// ── Product card in the selection list ──
const ProductCard = ({ product, isSelected, onToggle, imageBaseUrl }) => {
  const [imgError, setImgError] = useState(false);

  const imgSrc = useMemo(() => {
    if (!product.img1) return null;
    if (typeof product.img1 === 'string' && product.img1.startsWith('http')) return product.img1;
    if (typeof product.img1 === 'string' && product.img1.startsWith('/')) return `${imageBaseUrl}${product.img1}`;
    return product.img1;
  }, [product.img1, imageBaseUrl]);

  return (
    <div
      onClick={() => onToggle(product.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '8px 12px', borderRadius: '10px', cursor: 'pointer',
        background: isSelected ? '#f0f9ff' : 'transparent',
        border: isSelected ? '1.5px solid #93c5fd' : '1.5px solid transparent',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => {
        if (!isSelected) {
          e.currentTarget.style.background = '#f9fafb';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }
      }}
      onMouseLeave={e => {
        if (!isSelected) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'transparent';
        }
      }}
    >
      {/* Checkbox */}
      <div style={{
        width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isSelected ? '#3b82f6' : '#f3f4f6',
        border: isSelected ? 'none' : '1.5px solid #d1d5db',
        transition: 'all 0.15s ease',
      }}>
        {isSelected && <Check style={{ width: '12px', height: '12px', color: '#fff' }} />}
      </div>

      {/* Product image */}
      <div style={{
        width: '44px', height: '44px', borderRadius: '8px', overflow: 'hidden',
        background: '#f3f4f6', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {imgSrc && !imgError ? (
          <img
            src={imgSrc}
            alt={product.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <ImageIcon style={{ width: '18px', height: '18px', color: '#d1d5db' }} />
        )}
      </div>

      {/* Product info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontSize: '12.5px', fontWeight: 600, color: '#1f2937',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          lineHeight: 1.3,
        }}>
          {product.name}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#3b82f6' }}>
            {product.Discountedprice
              ? `\u20b9${Number(product.Discountedprice).toLocaleString('en-IN')}`
              : `\u20b9${Number(product.price || 0).toLocaleString('en-IN')}`}
          </span>
          {product.Discountedprice && product.price && (
            <span style={{
              fontSize: '10px', color: '#9ca3af', textDecoration: 'line-through',
            }}>
              {`\u20b9${Number(product.price).toLocaleString('en-IN')}`}
            </span>
          )}
          {product.category && (
            <span style={{
              fontSize: '10px', color: '#6b7280', background: '#f3f4f6',
              padding: '1px 6px', borderRadius: '4px',
            }}>
              {product.category}
            </span>
          )}
        </div>
      </div>

      {/* Stock indicator */}
      {product.in_stock === false && (
        <span style={{
          fontSize: '9px', fontWeight: 600, color: '#ef4444', textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}>
          Out of stock
        </span>
      )}
    </div>
  );
};

// ── Section target selector ──
const SectionTargetItem = ({ label, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer',
      background: isActive ? '#eff6ff' : 'transparent',
      color: isActive ? '#2563eb' : '#4b5563',
      fontSize: '12px', fontWeight: isActive ? 600 : 500,
      width: '100%', textAlign: 'left',
      transition: 'all 0.15s ease',
    }}
  >
    <span>{label}</span>
    <span style={{
      fontSize: '10px', fontWeight: 600, color: isActive ? '#3b82f6' : '#9ca3af',
      background: isActive ? '#dbeafe' : '#f3f4f6',
      padding: '2px 6px', borderRadius: '4px',
    }}>
      {count}
    </span>
  </button>
);

// ── Main Product Picker Panel ──
const ProductPickerPanel = ({
  designerProducts,
  designerCollections,
  designerCategories,
  templateJSON,
  onUpdateProducts,
  onClose,
  imageBaseUrl,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('products'); // products | featured_products | new_arrivals | bestsellers
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const content = templateJSON?.content || {};

  // Determine which content sections exist in the template that hold product data
  const productSections = useMemo(() => {
    const sections = [];
    const productKeys = ['products', 'featured_products', 'featuredProducts', 'new_arrivals', 'bestsellers'];
    for (const key of productKeys) {
      const val = content[key];
      if (val) {
        const items = Array.isArray(val) ? val : (val.items || []);
        sections.push({
          key,
          label: key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^\w/, c => c.toUpperCase()).trim(),
          count: items.length,
        });
      }
    }
    // If no product sections found, add a default one
    if (sections.length === 0) {
      sections.push({ key: 'products', label: 'Products', count: 0 });
    }
    return sections;
  }, [content]);

  // Get the current selection for the active section
  const currentSelection = useMemo(() => {
    const val = content[activeSection];
    if (!val) return [];
    const items = Array.isArray(val) ? val : (val.items || []);
    return items.map(item => item.id).filter(Boolean);
  }, [content, activeSection]);

  // Validate: check if any selected product IDs no longer exist in the product list
  const validProductIds = useMemo(() => new Set(designerProducts.map(p => p.id)), [designerProducts]);
  const staleProductIds = useMemo(
    () => currentSelection.filter(id => !validProductIds.has(id)),
    [currentSelection, validProductIds]
  );

  // Auto-clean stale products from the section when detected
  const handleCleanStale = useCallback(() => {
    const val = content[activeSection];
    if (!val) return;
    const isDict = val && !Array.isArray(val) && typeof val === 'object';
    const items = isDict ? (val.items || []) : (Array.isArray(val) ? val : []);
    const cleaned = items.filter(item => item.id && validProductIds.has(item.id));
    const newVal = isDict ? { ...val, items: cleaned } : cleaned;
    onUpdateProducts(activeSection, newVal);
  }, [content, activeSection, validProductIds, onUpdateProducts]);

  // Unique categories from products
  const categories = useMemo(() => {
    const cats = [...new Set(designerProducts.map(p => p.category).filter(Boolean))];
    return cats.sort();
  }, [designerProducts]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    let result = designerProducts;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    return result;
  }, [designerProducts, searchQuery, categoryFilter]);

  // Toggle product selection
  const handleToggle = useCallback((productId) => {
    const product = designerProducts.find(p => p.id === productId);
    if (!product) return;

    const val = content[activeSection];
    let items;
    const isDict = val && !Array.isArray(val) && typeof val === 'object';

    if (isDict) {
      items = [...(val.items || [])];
    } else if (Array.isArray(val)) {
      items = [...val];
    } else {
      items = [];
    }

    const existingIdx = items.findIndex(item => item.id === productId);
    if (existingIdx >= 0) {
      // Remove
      items.splice(existingIdx, 1);
    } else {
      // Add — map to the format used by templates
      items.push({
        id: product.id,
        name: product.name,
        price: product.Discountedprice
          ? `\u20b9${Number(product.Discountedprice).toLocaleString('en-IN')}`
          : `\u20b9${Number(product.price || 0).toLocaleString('en-IN')}`,
        originalPrice: product.Discountedprice && product.price
          ? `\u20b9${Number(product.price).toLocaleString('en-IN')}`
          : null,
        category: product.category || '',
        image: product.img1
          ? (typeof product.img1 === 'string' && product.img1.startsWith('http')
              ? product.img1
              : `${imageBaseUrl}${product.img1}`)
          : '',
        img1: product.img1
          ? (typeof product.img1 === 'string' && product.img1.startsWith('http')
              ? product.img1
              : `${imageBaseUrl}${product.img1}`)
          : '',
        in_stock: product.in_stock !== false,
      });
    }

    // Build updated content section value
    let newVal;
    if (isDict) {
      newVal = { ...val, items };
    } else {
      newVal = items;
    }

    onUpdateProducts(activeSection, newVal);
  }, [designerProducts, content, activeSection, onUpdateProducts, imageBaseUrl]);

  // Select all visible products
  const handleSelectAll = useCallback(() => {
    const val = content[activeSection];
    const isDict = val && !Array.isArray(val) && typeof val === 'object';

    const items = filteredProducts.map(product => ({
      id: product.id,
      name: product.name,
      price: product.Discountedprice
        ? `\u20b9${Number(product.Discountedprice).toLocaleString('en-IN')}`
        : `\u20b9${Number(product.price || 0).toLocaleString('en-IN')}`,
      originalPrice: product.Discountedprice && product.price
        ? `\u20b9${Number(product.price).toLocaleString('en-IN')}`
        : null,
      category: product.category || '',
      image: product.img1
        ? (typeof product.img1 === 'string' && product.img1.startsWith('http')
            ? product.img1
            : `${imageBaseUrl}${product.img1}`)
        : '',
      img1: product.img1
        ? (typeof product.img1 === 'string' && product.img1.startsWith('http')
            ? product.img1
            : `${imageBaseUrl}${product.img1}`)
        : '',
      in_stock: product.in_stock !== false,
    }));

    let newVal;
    if (isDict) {
      newVal = { ...val, items };
    } else {
      newVal = items;
    }

    onUpdateProducts(activeSection, newVal);
  }, [filteredProducts, content, activeSection, onUpdateProducts, imageBaseUrl]);

  // Clear all selections
  const handleClearAll = useCallback(() => {
    const val = content[activeSection];
    const isDict = val && !Array.isArray(val) && typeof val === 'object';
    const newVal = isDict ? { ...val, items: [] } : [];
    onUpdateProducts(activeSection, newVal);
  }, [content, activeSection, onUpdateProducts]);

  const selectedCount = currentSelection.length;

  return (
    <div style={{
      width: '320px', background: '#ffffff', borderLeft: '1px solid #e5e7eb',
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid #e5e7eb',
        background: '#fafbfc',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShoppingBag style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
          <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#111827', margin: 0 }}>
            Product Picker
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '6px', border: 'none', cursor: 'pointer',
              background: 'transparent', color: '#6b7280',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
          >
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        )}
      </div>

      {/* ── No products state ── */}
      {designerProducts.length === 0 && (
        <div style={{
          padding: '40px 24px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
        }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Package style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
          </div>
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', margin: '0 0 4px' }}>
              No products yet
            </p>
            <p style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
              Upload products from your dashboard first, then come back here to feature them on your homepage.
            </p>
          </div>
        </div>
      )}

      {designerProducts.length > 0 && (
        <>
          {/* ── Section targets ── */}
          {productSections.length > 1 && (
            <div style={{
              padding: '8px 12px', borderBottom: '1px solid #f3f4f6',
            }}>
              <p style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px 4px' }}>
                Assign to section
              </p>
              {productSections.map(section => (
                <SectionTargetItem
                  key={section.key}
                  label={section.label}
                  count={section.count}
                  isActive={activeSection === section.key}
                  onClick={() => setActiveSection(section.key)}
                />
              ))}
            </div>
          )}

          {/* ── Search + Filter ── */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '7px 10px', borderRadius: '8px', border: '1px solid #e5e7eb',
              background: '#f9fafb',
            }}>
              <Search style={{ width: '14px', height: '14px', color: '#9ca3af', flexShrink: 0 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  fontSize: '12px', color: '#374151', width: '100%',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    width: '18px', height: '18px', borderRadius: '4px', border: 'none',
                    background: '#e5e7eb', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <X style={{ width: '10px', height: '10px', color: '#6b7280' }} />
                </button>
              )}
            </div>

            {/* Category filter */}
            {categories.length > 1 && (
              <div style={{ marginTop: '6px', position: 'relative' }}>
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '4px 8px', borderRadius: '6px',
                    border: '1px solid #e5e7eb', background: categoryFilter !== 'all' ? '#eff6ff' : '#fff',
                    cursor: 'pointer', fontSize: '11px', fontWeight: 500,
                    color: categoryFilter !== 'all' ? '#2563eb' : '#6b7280',
                  }}
                >
                  {categoryFilter !== 'all' ? categoryFilter : 'All categories'}
                  <ChevronDown style={{ width: '12px', height: '12px' }} />
                </button>
                {showCategoryDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 10,
                    minWidth: '160px', maxHeight: '200px', overflow: 'auto',
                    padding: '4px',
                  }}>
                    <button
                      onClick={() => { setCategoryFilter('all'); setShowCategoryDropdown(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                        background: categoryFilter === 'all' ? '#eff6ff' : 'transparent',
                        color: '#374151', fontSize: '11px', fontWeight: categoryFilter === 'all' ? 600 : 400,
                      }}
                    >
                      All categories
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => { setCategoryFilter(cat); setShowCategoryDropdown(false); }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '6px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                          background: categoryFilter === cat ? '#eff6ff' : 'transparent',
                          color: '#374151', fontSize: '11px', fontWeight: categoryFilter === cat ? 600 : 400,
                        }}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selection info bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginTop: '8px',
            }}>
              <span style={{ fontSize: '11px', color: '#6b7280' }}>
                {selectedCount} selected of {designerProducts.length}
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={handleSelectAll}
                  style={{
                    fontSize: '10px', fontWeight: 600, color: '#3b82f6',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '2px 4px', textDecoration: 'underline',
                  }}
                >
                  Select all
                </button>
                {selectedCount > 0 && (
                  <button
                    onClick={handleClearAll}
                    style={{
                      fontSize: '10px', fontWeight: 600, color: '#ef4444',
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '2px 4px', textDecoration: 'underline',
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Stale product warning ── */}
          {staleProductIds.length > 0 && (
            <div style={{
              margin: '8px 12px', padding: '10px 12px', borderRadius: '8px',
              background: '#fef3c7', border: '1px solid #fbbf24',
              display: 'flex', alignItems: 'flex-start', gap: '8px',
            }}>
              <AlertCircle style={{ width: '16px', height: '16px', color: '#d97706', flexShrink: 0, marginTop: '1px' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', fontWeight: 600, color: '#92400e', margin: '0 0 4px' }}>
                  {staleProductIds.length} selected product{staleProductIds.length > 1 ? 's' : ''} no longer exist{staleProductIds.length === 1 ? 's' : ''}
                </p>
                <button
                  onClick={handleCleanStale}
                  style={{
                    fontSize: '10px', fontWeight: 600, color: '#d97706',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 0, textDecoration: 'underline',
                  }}
                >
                  Remove stale products
                </button>
              </div>
            </div>
          )}

          {/* ── Product list ── */}
          <div style={{
            flex: 1, overflow: 'auto', padding: '4px 8px',
          }}>
            {filteredProducts.length === 0 && (
              <div style={{
                padding: '24px 16px', textAlign: 'center',
              }}>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                  {searchQuery ? 'No products match your search.' : 'No products available.'}
                </p>
              </div>
            )}
            {filteredProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                isSelected={currentSelection.includes(product.id)}
                onToggle={handleToggle}
                imageBaseUrl={imageBaseUrl}
              />
            ))}
          </div>

          {/* ── Footer hint ── */}
          <div style={{
            padding: '10px 16px', borderTop: '1px solid #f3f4f6',
            background: '#fefce8',
          }}>
            <p style={{
              fontSize: '11px', color: '#92400e', lineHeight: 1.5, margin: 0,
              display: 'flex', alignItems: 'flex-start', gap: '6px',
            }}>
              <Sparkles style={{ width: '14px', height: '14px', flexShrink: 0, marginTop: '1px' }} />
              Selected products will replace placeholder data on your live site. Remember to save.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductPickerPanel;
